import { useCallback, useEffect, useRef, useState } from 'react';

// Picture-in-picture when you leave the app during a video call (M70, D268).
//
// - Document Picture-in-Picture (desktop Chrome/Edge 116+): a small window
//   with both videos and mute / hang-up; the caller renders into `pipWindow`.
// - Element PiP (`requestPictureInPicture`, Chrome, Edge, Safari): the main
//   remote video only.
// - Browsers want a user gesture for either. We try when the page is
//   hidden (it works where the browser allows it), register the Media
//   Session `enterpictureinpicture` action (Chrome's automatic PiP for
//   video calls calls it, with the gesture granted), and mark the video
//   `autopictureinpicture` (Safari). The call screen also has a PiP button,
//   which is a gesture everywhere.
//
// Leaving PiP, by closing it or coming back to the app, returns to the
// minimised tile; it never ends the call.

export interface PipSupport {
  element: boolean;
  documentWindow: boolean;
}

interface DocumentPictureInPicture {
  requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
  window: Window | null;
}

function documentPip(): DocumentPictureInPicture | null {
  return (
    (window as Window & { documentPictureInPicture?: DocumentPictureInPicture })
      .documentPictureInPicture ?? null
  );
}

export function pipSupport(): PipSupport {
  const element =
    typeof document !== 'undefined' &&
    document.pictureInPictureEnabled === true &&
    typeof HTMLVideoElement !== 'undefined' &&
    typeof HTMLVideoElement.prototype.requestPictureInPicture === 'function';
  return { element, documentWindow: documentPip() !== null };
}

export function canPip(support: PipSupport): boolean {
  return support.element || support.documentWindow;
}

/** Only a video call that's connected, with someone's picture to show. */
export function shouldEnterPip(input: {
  hidden: boolean;
  active: boolean;
  hasRemoteVideo: boolean;
  inPip: boolean;
  support: PipSupport;
}): boolean {
  return (
    input.hidden &&
    input.active &&
    input.hasRemoteVideo &&
    !input.inPip &&
    canPip(input.support)
  );
}

export type PipMode = 'document' | 'element' | null;

const DOC_PIP_SIZE = { width: 320, height: 420 };

export interface CallPipOptions {
  /** A video call is connected. When it stops, any PiP is closed. */
  active: boolean;
  /** The main remote video's element, when it has a picture. */
  video: HTMLVideoElement | null;
  /** Back to the minimised tile (D268). Never ends the call. */
  onLeave: () => void;
}

export function useCallPip({ active, video, onLeave }: CallPipOptions) {
  const [mode, setMode] = useState<PipMode>(null);
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const [support] = useState(pipSupport);
  const modeRef = useRef<PipMode>(null);
  const opening = useRef(false);
  // Closing PiP because the person tapped the tile: they want the full
  // call, not the tile (S1).
  const expanding = useRef(false);
  const latest = useRef({ active, video, onLeave });
  latest.current = { active, video, onLeave };

  const left = useCallback(() => {
    const toFullScreen = expanding.current;
    expanding.current = false;
    modeRef.current = null;
    setMode(null);
    setPipWindow(null);
    // Back to the tile only while the call is still live: a call that ended
    // in PiP (hung up there, or by the others while away) must not leave the
    // next call starting minimised.
    if (!toFullScreen && latest.current.active) latest.current.onLeave();
  }, []);

  // A video went into PiP: by our request, or by itself (Safari's
  // autopictureinpicture). Either way its leaving brings back the tile.
  const tookElement = useCallback(
    (target: HTMLVideoElement) => {
      if (modeRef.current) return;
      modeRef.current = 'element';
      setMode('element');
      target.addEventListener('leavepictureinpicture', left, { once: true });
    },
    [left]
  );

  const enter = useCallback(async (): Promise<PipMode> => {
    const { active: isActive, video: target } = latest.current;
    if (!isActive || modeRef.current || opening.current) return modeRef.current;
    opening.current = true;
    expanding.current = false;
    try {
      const docPip = documentPip();
      if (docPip) {
        try {
          const win = await docPip.requestWindow(DOC_PIP_SIZE);
          win.document.body.style.margin = '0';
          win.document.body.style.background = '#0F0E0D';
          win.document.title = document.title;
          win.addEventListener('pagehide', left, { once: true });
          modeRef.current = 'document';
          setMode('document');
          setPipWindow(win);
          return 'document';
        } catch {
          // No gesture, or refused: try the plain video next.
        }
      }
      if (support.element && target) {
        try {
          await target.requestPictureInPicture();
          tookElement(target);
          return 'element';
        } catch {
          // Not allowed without a gesture here; the call carries on as audio.
        }
      }
      return null;
    } finally {
      opening.current = false;
    }
  }, [left, tookElement, support.element]);

  /**
   * Closes PiP. With `toFullScreen` (tapping the tile) the person lands on
   * the full call; the videos come back to the page at once.
   */
  const exit = useCallback((options?: { toFullScreen?: boolean }) => {
    if (!modeRef.current) return;
    if (options?.toFullScreen) expanding.current = true;
    if (modeRef.current === 'document') {
      const win = documentPip()?.window;
      if (options?.toFullScreen) {
        modeRef.current = null;
        setMode(null);
        setPipWindow(null);
      }
      win?.close();
    } else if (
      modeRef.current === 'element' &&
      document.pictureInPictureElement
    ) {
      void document.exitPictureInPicture().catch(() => undefined);
    }
  }, []);

  // Leaving the app opens PiP; coming back closes it (back to the tile).
  useEffect(() => {
    const onVisibility = () => {
      const hidden = document.visibilityState === 'hidden';
      if (
        shouldEnterPip({
          hidden,
          active: latest.current.active,
          hasRemoteVideo: latest.current.video !== null,
          inPip: modeRef.current !== null,
          support,
        })
      ) {
        void enter();
      } else if (!hidden && modeRef.current) {
        exit();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [enter, exit, support]);

  // Chrome's automatic picture-in-picture for video calls (desktop) asks
  // through the Media Session; unknown actions throw in other browsers.
  useEffect(() => {
    if (!active || !('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.setActionHandler(
        'enterpictureinpicture' as MediaSessionAction,
        () => void enter()
      );
    } catch {
      return;
    }
    return () => {
      try {
        navigator.mediaSession.setActionHandler(
          'enterpictureinpicture' as MediaSessionAction,
          null
        );
      } catch {
        // Nothing to undo.
      }
    };
  }, [active, enter]);

  // Safari goes into PiP by itself when you leave with this set.
  useEffect(() => {
    if (!active || !video) return;
    video.setAttribute('autopictureinpicture', '');
    const onEnter = () => tookElement(video);
    video.addEventListener('enterpictureinpicture', onEnter);
    return () => {
      video.removeAttribute('autopictureinpicture');
      video.removeEventListener('enterpictureinpicture', onEnter);
    };
  }, [active, video, tookElement]);

  // The call ended: close whatever is open.
  useEffect(() => {
    if (!active && modeRef.current) exit();
  }, [active, exit]);

  return {
    mode,
    pipWindow,
    supported: canPip(support),
    enter,
    exit,
  };
}
